import React from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { intl } from "LocaleWrapper";
import { useQuery } from "@apollo/client";
import { useStoreState, Session } from "ezStore";

import { PageHeader, Card, FormGroup, Table, Button } from "@cofano/cck";
import { CircularProgress, Grid2 as Grid, Box, Tabs, Tab, Typography } from "@mui/material";
import Info from "@mui/icons-material/Info";

import useTabs from "components/hooks/useTabs";
import { OpeningHoursDetails } from "components/openinghours/OpeningHours";
import Helmet from "components/helmet/Helmet";
import { TableId } from "components/table";

import { Language, usePermission, hasPluginPermission, hasOptionalFieldPermission, Fields, DataFormat } from "imports/Utils";

import { LocationLink, RelationLink, ShipLink } from "utils/LinkUtils";

import ContactRoleTypeTags from "view/relations/components/tags/ContactRoleTypeTags";
import ProductsTable from "view/products/components/table/ProductsTable";

import {
	RelationTabs,
	showCommunicateStatusChanges,
	showContactRoleType,
	showContactsTab,
	showDefaultContact,
	showVisibleForShip,
} from "./utils/RelationUtils";

import { ChildProps } from "types/commons";

import { GetRelationDocument, GetRelationQuery } from "__generated__/view/relations/graphql/relation.queries.generated";
import { GetInvoiceGroupTypesDocument } from "__generated__/view/invoicegrouptypes/graphql/invoiceGroupType.queries.generated";

export type RelationFromQuery = ChildProps<GetRelationQuery, "relation">;

const RelationDetails = () => {

	const { isRTR, isMasterdata, isReadOnly, isSkipperOrShipManager, isFinance } = usePermission();
	const hasPermission = !isReadOnly && (isRTR || isMasterdata);
	const hasInvoiceGroupTypePermission = isRTR || isMasterdata || isFinance;

	const { id } = useParams();
	const navigate = useNavigate();
	const location = useLocation();
	const { data: invoiceGroupData } = hasInvoiceGroupTypePermission ? useQuery(GetInvoiceGroupTypesDocument) : {};

	const session = useStoreState(store => store.session);

	const { selectedTab, selectTab } = useTabs<RelationTabs>({
		defaultSelected: location.state?.tab || RelationTabs.General,
	});

	const { data, loading } = useQuery(GetRelationDocument, {
		variables: {
			id: Number(id),
		},
	});

	const relation = data?.relation;

	if (!relation || loading) {
		return <CircularProgress size={24} />;
	}

	const { customer, isShipManager, isSkipper, operator } = relation;

	return (
		<div className="relation-details">
			<PageHeader>
				<PageHeader.Title>
					{Language("RELATION")}
					<Helmet title={intl.formatMessage({ id: "RELATION" })} />
				</PageHeader.Title>

				<PageHeader.Actions>
					{ isRTR && (
						<Button onClick={() => navigate("/view/relations")} variant="outlined">
							{intl.formatMessage({ id: "RELATIONS" })}
						</Button>
					)}

					{hasPermission && (
						<Button onClick={() => navigate(`/view/relations/${relation.id}/edit`, { state: { tab: selectedTab } })}>
							{intl.formatMessage({ id: "EDIT" })}
						</Button>
					)}
				</PageHeader.Actions>
			</PageHeader>

			<Grid container spacing={3}>
				<Grid size={{ xs: 12, md: 5 }}>
					<Card title={Language("DETAILS")} icon={<Info />}>
						<Details relation={relation} session={session} />
					</Card>
				</Grid>

				<Grid size={{ xs: 12, md: 7 }}>
					<Card disablePadding>
						<Box sx={{ borderBottom: 1, borderColor: "divider" }}>
							<Tabs
								variant='scrollable'
								value={selectedTab}
								onChange={(_event, newValue) => selectTab(newValue)}>
								<Tab label={Language("GENERAL")} value={RelationTabs.General} />

								{hasOptionalFieldPermission(session, Fields.RELATION, "workingHours") && (
									<Tab label={Language("WORKING_HOURS")} value={RelationTabs.WorkingHours} />
								)}

								{hasOptionalFieldPermission(session, Fields.RELATION, "contacts") && showContactsTab(relation) && (
									<Tab label={Language("CONTACTS")} value={RelationTabs.Contacts} />
								)}

								{isShipManager && (
									<Tab label={Language("SHIP_MANAGER_SHIPS")} value={RelationTabs.ShipManagerShips} />
								)}

								{isSkipper && (
									<Tab label={Language("SKIPPER_SHIPS")} value={RelationTabs.SkipperShips} />
								)}

								{operator && (
									<Tab label={Language("OPERATORS_SHIPS")} value={RelationTabs.OperatorShips} />
								)}

								{((hasPluginPermission(session, "customerLocations") && customer) || (hasPluginPermission(session, "locationRelations") && relation.isLocationRelation)) && (
									<Tab label={Language("LOCATIONS")} value={RelationTabs.Locations} />
								)}

								{hasOptionalFieldPermission(session, Fields.RELATION, "products") && (customer || relation.supplier) && (
									<Tab label={Language("PRODUCTS")} value={RelationTabs.Products} />
								)}

								{customer && (
									<Tab label={Language("LINKED_CUSTOMERS")} value={RelationTabs.LinkedCustomers} />
								)}
							</Tabs>
						</Box>

						<div style={{ padding: 16 }}>
							{selectedTab === RelationTabs.General && (
								<Grid container spacing={3}>
									<React.Fragment>
										<Grid size={{ lg: 6, md: 6, xs: 12 }}>
											<Typography
												variant="h4"
												sx={{ fontSize: 16 }}
												color="textPrimary"
												mb={2}
											>
												{Language("ADDRESS")}
											</Typography>

											{hasOptionalFieldPermission(session, Fields.ADDRESS, "town") && (
												<FormGroup label={Language("CLIENT_CITY")} labelPlacement="left-center" overflow="hidden">
													{relation.address?.town || "-"}
												</FormGroup>
											)}

											{hasOptionalFieldPermission(session, Fields.ADDRESS, "street") && (
												<FormGroup label={Language("CLIENT_STREET")} labelPlacement="left-center" overflow="hidden">
													{relation.address?.street || "-"}
												</FormGroup>
											)}

											{hasOptionalFieldPermission(session, Fields.ADDRESS, "zipcode") && (
												<FormGroup label={Language("CLIENT_ZIPCODE")} labelPlacement="left-center" overflow="hidden">
													{relation.address?.zipCode || "-"}
												</FormGroup>
											)}

											{hasOptionalFieldPermission(session, Fields.ADDRESS, "name1") && (
												<FormGroup label={Language("CLIENT_NAME_ONE")} labelPlacement="left-center" overflow="hidden">
													{relation.address?.name1 || "-"}
												</FormGroup>
											)}

											{hasOptionalFieldPermission(session, Fields.ADDRESS, "name2") && (
												<FormGroup label={Language("CLIENT_NAME_TWO")} labelPlacement="left-center" overflow="hidden">
													{relation.address?.name2 || "-"}
												</FormGroup>
											)}

											{hasOptionalFieldPermission(session, Fields.ADDRESS, "country") && (
												<FormGroup label={Language("CLIENT_COUNTRY")} labelPlacement="left-center" overflow="hidden">
													{relation.address?.country || "-"}
												</FormGroup>
											)}
										</Grid>

										{hasPluginPermission(session, "invoices") && (
											<Grid size={{ lg: 6, md: 6, xs: 12 }}>
												<Typography
													variant="h4"
													sx={{ fontSize: 16 }}
													color="textPrimary"
													mb={2}
												>
													{Language("RELATION_BANK_DETAILS")}
												</Typography>

												<FormGroup label={Language("IVENT_ACCOUNT_HOLDER")} labelPlacement="left-center" overflow="hidden">
													{relation.invoiceDetails?.accountName || "-"}
												</FormGroup>

												<FormGroup label={Language("IVENT_IBAN")} labelPlacement="left-center" overflow="hidden">
													{relation.invoiceDetails?.iban || "-"}
												</FormGroup>

												<FormGroup label={Language("IVENT_BIC")} labelPlacement="left-center" overflow="hidden">
													{relation.invoiceDetails?.bic || "-"}
												</FormGroup>

												<FormGroup label={Language("IVENT_VAT_REFERENCE")} labelPlacement="left-center" overflow="hidden">
													{relation.invoiceDetails?.vatReference || "-"}
												</FormGroup>

												<FormGroup label={Language("IVENT_COC_REFERENCE")} labelPlacement="left-center" overflow="hidden">
													{relation.invoiceDetails?.cocReference || "-"}
												</FormGroup>
											</Grid>
										)}

										{(invoiceGroupData?.invoiceGroupTypes.length ?? 0) > 0 && (
											<Grid size={{ lg: 6, md: 6, xs: 12 }}>
												<h4>{Language("INVOICING")}</h4>
												{invoiceGroupData?.invoiceGroupTypes.map(
													(group) => <>
														<FormGroup label={group.name} labelPlacement="left-center" overflow="hidden">
															<RelationLink relation={relation.invoiceGroupLinks?.find(l => l.invoiceGroupType.id === group.id)?.linkedRelation ?? relation} />
														</FormGroup>
													</>,
												)}
											</Grid>
										)}
									</React.Fragment>
								</Grid>
							)}

							{selectedTab === RelationTabs.WorkingHours && (
								<OpeningHoursDetails hours={relation.workingHours ?? null} />
							)}

							{selectedTab === RelationTabs.Contacts && (
								<Table
									id="relationContactsTable"
									content={relation.contacts || []}
									columns={[ {
										id: "type",
										title: intl.formatMessage({ id: "TYPE" }),
										disabled: relation.contacts.every(x => !x.type),
									}, {
										id: "contactRoleType",
										title: intl.formatMessage({ id: "CONTACT_ROLE_TYPE" }),
										render: (row) => <ContactRoleTypeTags contact={row} />,
										width: 280,
										disabled: !showContactRoleType(relation),
									}, {
										id: "name",
										title: intl.formatMessage({ id: "SHIPS_NAME" }),
									}, {
										id: "insertion",
										title: intl.formatMessage({ id: "INSERTION" }),
										disabled: relation.contacts.every(x => !x.insertion),
									}, {
										id: "surName",
										title: intl.formatMessage({ id: "SURNAME" }),
										disabled: relation.contacts.every(x => !x.surName),
									}, {
										id: "email",
										title: intl.formatMessage({ id: "EMAIL" }),
									}, {
										id: "telephone",
										title: intl.formatMessage({ id: "TELEPHONE" }),
										disabled: relation.contacts.every(x => !x.telephone),
									}, {
										id: "defaultContact",
										title: intl.formatMessage({ id: "DEFAULT_CONTACT" }),
										render: (row) => DataFormat.boolean(row.defaultContact ?? false),
										disabled: !showDefaultContact(relation),
									}, {
										id: "visibleForShip",
										title: intl.formatMessage({ id: "VISIBLE_FOR_SHIP" }),
										defaultVisible: !isSkipperOrShipManager,
										render: (row) => DataFormat.boolean(row.visibleForShip ?? false),
										disabled: !showVisibleForShip(relation),
									}, {
										id: "communicateStatusChanges",
										title: intl.formatMessage({ id: "COMMUNICATE_STATUS_CHANGES" }),
										defaultVisible: !isSkipperOrShipManager,
										disabled: !showCommunicateStatusChanges(relation, session),
										render: (row) => DataFormat.boolean(row.communicateStatusChanges ?? false),
									},
									]}
									disableFooter />
							)}

							{selectedTab === RelationTabs.ShipManagerShips && (
								<Table
									id="relationManagedShipsTable"
									content={relation.managedShips ?? []}
									columns={[ {
										id: "name",
										title: intl.formatMessage({ id: "SHIPS_NAME" }),
										render: row => <ShipLink ship={row} />,
									}, {
										id: "eni",
										title: intl.formatMessage({ id: "SHIP_ADD_ENI" }),
									}, {
										id: "mmsi",
										title: intl.formatMessage({ id: "SHIP_ADD_MMSI" }),
									} ]}
									disableFooter />
							)}

							{selectedTab === RelationTabs.SkipperShips && (
								<Table
									id="relationSkipperShipsTable"
									content={relation.skipperShip ? [ relation.skipperShip ] : []}
									columns={[ {
										id: "name",
										title: intl.formatMessage({ id: "SHIPS_NAME" }),
										render: row => <ShipLink ship={row} />,
									}, {
										id: "eni",
										title: intl.formatMessage({ id: "SHIP_ADD_ENI" }),
									}, {
										id: "mmsi",
										title: intl.formatMessage({ id: "SHIP_ADD_MMSI" }),
									} ]}
									disableFooter />
							)}

							{selectedTab === RelationTabs.OperatorShips && (
								<Table
									id="relationOperatorShipsTable"
									content={relation.operatorShips || []}
									columns={[ {
										id: "name",
										title: intl.formatMessage({ id: "SHIPS_NAME" }),
										render: row => <ShipLink ship={row} />,
									}, {
										id: "eni",
										title: intl.formatMessage({ id: "SHIP_ADD_ENI" }),
									}, {
										id: "mmsi",
										title: intl.formatMessage({ id: "SHIP_ADD_MMSI" }),
									} ]}
									disableFooter />
							)}

							{selectedTab === RelationTabs.Locations && (
								<Table
									id="relationLocationsTable"
									content={(relation.locations || []).map((it, index) => ({ ...it, id: index }))}
									columns={[ {
										id: "location.description",
										title: intl.formatMessage({ id: "LOCATION" }),
										render: (row) => <LocationLink location={row.location} />,
									}, {
										id: "type",
										title: intl.formatMessage({ id: "TYPE" }),
										render: (row) => intl.formatMessage({ id: `LOCATION_${row.type}` }),
									} ]}
									disableFooter />
							)}

							{selectedTab === RelationTabs.Products  && (
								<ProductsTable tableId={TableId.ProductsSupplier} supplierId={relation.id} />
							)}

							{selectedTab === RelationTabs.LinkedCustomers && (
								<Table
									id="relationLinkedCustomersTable"
									content={relation.linkedRelations || []}
									columns={[ {
										id: "name",
										title: intl.formatMessage({ id: "SHIPS_NAME" }),
									}, {
										id: "abbreviation",
										title: intl.formatMessage({ id: "CLIENT_ABBREVIATION" }),
									}, {
										id: "email",
										title: intl.formatMessage({ id: "EMAIL" }),
									} ]}
									disableFooter />
							)}
						</div>
					</Card>

					<div style={{ marginTop: 16 }}>
						<Card title={Language("REMARKS")}>
							{DataFormat.remarks(relation.remarks, "-")}
						</Card>
					</div>
				</Grid>
			</Grid>
		</div>
	);
};

const Details = ({ relation, session }: { relation: RelationFromQuery; session: Session }) => {

	const isCustomer = relation.customer;
	const isShippingCompany = relation.shippingCompany;
	const isOperator = relation.operator;
	const isLocationRelation = relation.isLocationRelation;
	const isApplicationRelation = relation.applicationRelation;

	const customerProductGroups = session?.settings?.multiValues?.customerProductGroups;

	return (
		<React.Fragment>
			{hasOptionalFieldPermission(session, Fields.RELATION, "customerNumber") && isCustomer && (
				<FormGroup label={Language("CLIENT_NUMBER")} labelPlacement="left-center" overflow="hidden">
					{relation.customerNumber || "-"}
				</FormGroup>
			)}

			{hasOptionalFieldPermission(session, Fields.RELATION, "externalId") && isCustomer && (
				<FormGroup label={Language("EXTERNAL_ID")} labelPlacement="left-center" overflow="hidden">
					{relation.externalId || "-"}
				</FormGroup>
			)}

			<FormGroup label={Language("CLIENT_CODE")} labelPlacement="left-center" overflow="hidden">
				{relation.name || "-"}
			</FormGroup>

			<FormGroup label={Language("EMAIL")} labelPlacement="left-center" overflow="hidden">
				{relation.email || "-"}
			</FormGroup>

			<FormGroup label={Language("CLIENT_LANGUAGE")} labelPlacement="left-center" overflow="hidden">
				{relation.language?.code || "-"}
			</FormGroup>

			<FormGroup label={Language("CLIENT_ABBREVIATION")} labelPlacement="left-center" overflow="hidden">
				{relation.abbreviation || "-"}
			</FormGroup>

			{isShippingCompany && (
				<FormGroup label={Language("CLIENT_SCAC")} labelPlacement="left-center" overflow="hidden">
					{relation.scac || "-"}
				</FormGroup>
			)}

			{hasOptionalFieldPermission(session, Fields.RELATION, "ean") && isOperator && (
				<FormGroup label={Language("OPERATORS_EAN")} labelPlacement="left-center" overflow="hidden">
					{relation.ean || "-"}
				</FormGroup>
			)}

			{customerProductGroups && isCustomer && (
				<FormGroup label={Language("CLIENT_PRODUCT_GROUP")} labelPlacement="left-center" overflow="hidden">
					{relation.productGroup || "-"}
				</FormGroup>
			)}

			{hasOptionalFieldPermission(session, Fields.RELATION, "externalCode") && (isCustomer || isOperator || isApplicationRelation) && (
				<FormGroup label={Language("EXTERNAL_CODE")} labelPlacement="left-center" overflow="hidden">
					{relation.externalCode || "-"}
				</FormGroup>
			)}

			{hasOptionalFieldPermission(session, Fields.RELATION, "externalCustomerNumber") && (isCustomer || isOperator) && (
				<FormGroup label={Language("EXTERNAL_CUSTOMER_NUMBER")} labelPlacement="left-center" overflow="hidden">
					{relation.externalCustomerNumber || "-"}
				</FormGroup>
			)}

			{hasOptionalFieldPermission(session, Fields.RELATION, "organisation") && isCustomer && (
				<FormGroup label={Language("CLIENT_ORGANISATION")} labelPlacement="left-center" overflow="hidden">
					{relation.organisation || "-"}
				</FormGroup>
			)}

			{hasOptionalFieldPermission(session, Fields.RELATION, "telephone") && (
				<FormGroup label={Language("PHONE_NUMBER")} labelPlacement="left-center" overflow="hidden">
					{relation.telephone || "-"}
				</FormGroup>
			)}

			{hasOptionalFieldPermission(session, Fields.RELATION, "volumePerYear") && isCustomer && (
				<FormGroup label={Language("CLIENT_VOLUME_A_YEAR")} labelPlacement="left-center" overflow="hidden">
					{relation.volumePerYear || "-"}
				</FormGroup>
			)}

			{hasOptionalFieldPermission(session, Fields.RELATION, "transportFrequency") && isCustomer && (
				<FormGroup label={Language("CLIENT_TRANSPORT_FREQUENCY")} labelPlacement="left-center" overflow="hidden">
					{relation.transportFrequency || "-"}
				</FormGroup>
			)}

			<FormGroup label={Language("CLIENT_SINGLE")} labelPlacement="left-center" overflow="hidden">
				{DataFormat.boolean(relation.customer)}
			</FormGroup>

			{hasOptionalFieldPermission(session, Fields.ORDER, "supplier") && (
				<FormGroup label={Language("SUPPLIER")} labelPlacement="left-center" overflow="hidden">
					{DataFormat.boolean(relation.supplier)}
				</FormGroup>
			)}

			<FormGroup label={Language("OPERATOR")} labelPlacement="left-center" overflow="hidden">
				{DataFormat.boolean(relation.operator)}
			</FormGroup>

			<FormGroup label={Language("SHIP_MANAGER")} labelPlacement="left-center" overflow="hidden">
				{DataFormat.boolean(relation.isShipManager)}
			</FormGroup>

			<FormGroup label={Language("SKIPPER")} labelPlacement="left-center" overflow="hidden">
				{DataFormat.boolean(relation.isSkipper)}
			</FormGroup>

			{hasPluginPermission(session, "shippingCompanies") && !isLocationRelation && (
				<FormGroup label={Language("SHIPPING_COMPANY")} labelPlacement="left-center" overflow="hidden">
					{DataFormat.boolean(relation.shippingCompany)}
				</FormGroup>
			)}

			<FormGroup label={Language("APPLICATION_RELATION")} labelPlacement="left-center" overflow="hidden">
				{DataFormat.boolean(relation.applicationRelation)}
			</FormGroup>

			{hasPluginPermission(session, "locationRelations") && (
				<FormGroup label={Language("LOCATION_MANAGER")} labelPlacement="left-center" overflow="hidden">
					{DataFormat.boolean(relation.isLocationRelation)}
				</FormGroup>
			)}

			{hasPluginPermission(session, "agents") && (
				<FormGroup label={Language("ORDER_AGENT")} labelPlacement="left-center" overflow="hidden">
					{DataFormat.boolean(relation.agent)}
				</FormGroup>
			)}

			{hasOptionalFieldPermission(session, Fields.RELATION, "isConsignor") && (
				<FormGroup label={Language("CLIENT_IS_CONSIGNOR")} labelPlacement="left-center" overflow="hidden">
					{DataFormat.boolean(relation.consignor)}
				</FormGroup>
			)}

			{hasPluginPermission(session, "containerOperators") && (
				<FormGroup label={Language("CONTAINER_OPERATOR")} labelPlacement="left-center" overflow="hidden">
					{DataFormat.boolean(relation.containerOperator)}
				</FormGroup>
			)}

			{hasPluginPermission(session, "brokers") && (
				<FormGroup label={Language("BROKER")} labelPlacement="left-center" overflow="hidden">
					{DataFormat.boolean(relation.broker)}
				</FormGroup>
			)}

			{hasPluginPermission(session, "surveyors") && (
				<FormGroup label={Language("SURVEYOR")} labelPlacement="left-center" overflow="hidden">
					{DataFormat.boolean(relation.surveyor)}
				</FormGroup>
			)}

			{hasOptionalFieldPermission(session, Fields.RELATION, "maxCargoDeviationForProtest") && (
				<FormGroup label={Language("MAX_CARGO_DEVIATION_FOR_LETTER_PROTEST")} labelPlacement="left-center" overflow="visible">
					{DataFormat.percentage(relation.maxCargoDeviationForLetterOfProtest)}
				</FormGroup>
			)}

			{hasOptionalFieldPermission(session, Fields.RELATION, "maxCargoDeviationForProtest") && (
				<FormGroup label={Language("MAX_CARGO_DEVIATION_FOR_CALL_KWKW")} labelPlacement="left-center" overflow="visible">
					{DataFormat.percentage(relation.maxCargoDeviationForCallKWKW)}
				</FormGroup>
			)}

			{hasOptionalFieldPermission(session, Fields.RELATION, "roundVatEachLine") && (
				<FormGroup label={Language("ROUND_VAT_EACH_LINE")} labelPlacement="left-center" overflow="hidden">
					{DataFormat.boolean(relation.roundVatEachLine)}
				</FormGroup>
			)}

			{hasOptionalFieldPermission(session, Fields.RELATION, "termsAndConditions") && isApplicationRelation && (
				<FormGroup label={Language("TERMS_AND_CONDITIONS_PURCHASE")} labelPlacement="left-center" overflow="hidden">
					{relation.termsAndConditionsPurchase || "-"}
				</FormGroup>
			)}

			{hasOptionalFieldPermission(session, Fields.RELATION, "termsAndConditions") && isApplicationRelation && (
				<FormGroup label={Language("TERMS_AND_CONDITIONS_SALE")} labelPlacement="left-center" overflow="hidden">
					{relation.termsAndConditionsSale || "-"}
				</FormGroup>
			)}

			<FormGroup
				label={Language("LOGO")}
				labelPlacement="left"
				labelPaddingTop={0}
				overflow="hidden">
				{relation.logo && (
					<img
						alt="Relation logo"
						style={{ maxWidth: "100%" }}
						src={`data:image/jpg|png|jpeg;base64,${relation.logo}`}
					/>
				)}
			</FormGroup>
		</React.Fragment>
	);
};

export default RelationDetails;
